import * as IoC from 'typescript-ioc';
import { runInAction, observable, action } from 'mobx';
import { IControlDAO, IControlMeetingRequestParams, IRegistrantDTO, IUpdateMeetingSubscriptionResponse } from '../../../../../../api/control.dao';
import { ParticipantsLiveFormTabBaseViewModel } from './partisipants-live-form-tab-base.viewmodel';
import { onUpdateMeetingControl } from '../../../../../../graphql/subscriptions';
import { subscribeAppsyncData } from '../../../../../../infrastructure/utils/appsync.utils';
import { IGNORE_ATTENDEES } from '../../../../../../api/organizations.dao';
import { IAttendeesLiveFormContainerProps } from '../attendees-live-form.container';
import { IPaginationParams } from '../../../../../../api/shared.dto';
import { handleApiException } from '../../../../../global/modal/alerts-host/alert-templates';
import { MINIMAL_SEARCH_LENGTH } from '../../../../../../constants/common.constants';
import { TRACKING_EVENTS } from '../../../../../../tracking-service/tracking-values';
import TrackingServiceProvider from '../../../../../../tracking-service/tracking-service-provider';
import { IGlobalStorage } from '../../../../../../infrastructure/global-storage';
import { AttendeeColumnPopoverViewmodel } from '../../../../../global/attendee-column/attendee-column-popover/attendee-column-popover.viewmodel';
import { mapRegistrantDtoToRegistrant } from '../../../../../../infrastructure/utils/joined-participants.utils';
import { FIRST_PAGE_INDEX } from '../../../../../../constants/pagination.contants';

export interface IRegistrant extends Omit<IRegistrantDTO, 'name'> {
    firstName: string;
    lastName: string;
}

class ParticipantsLiveFormRegistrantsTabViewModel extends ParticipantsLiveFormTabBaseViewModel<IRegistrant> {
    @observable
    public searchQuery: string;
    @observable
    public fetchingParticipants: boolean;
    @observable
    private attendeeDetailsPopoverVM: AttendeeColumnPopoverViewmodel;
    constructor(
        props: IAttendeesLiveFormContainerProps,
        @IoC.Inject private readonly controlDAO: IControlDAO = IoC.Container.get(IControlDAO),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
        @IoC.Inject private readonly globalStorage: IGlobalStorage = IoC.Container.get(IGlobalStorage),
    ) {
        super();
        super.initialize(props);
        this.searchQuery = '';
        this.fetchingParticipants = false;

        this.attendeeDetailsPopoverVM = props.attendeeDetailsPopoverVM;
    }
    public get attendeeDetailsVM(): AttendeeColumnPopoverViewmodel { return this.attendeeDetailsPopoverVM; }

    public subscribe = async (): Promise<void> => {
        this.subscription = await subscribeAppsyncData<IUpdateMeetingSubscriptionResponse>(
            onUpdateMeetingControl,
            { meetingId: this.meetingId },
            () => {
                // this.updateMeetingLiveData(updatedMeetingData.registrantsCount);
                // this.debouncedFetchParticipants();
            },
        );
    };

    @action
    public setSearchQuery = (query: string): void => {
        this.searchQuery = query;

        if (this.isSearchQueryValid()) {
            this.setActiveTab();
            this.fetchParticipants();
            this.trackSearchRegistrantsMixPanel();
        }
    };

    public isSearchQueryValid(): boolean {
        return this.searchQuery.trim().length >= MINIMAL_SEARCH_LENGTH;
    }

    public isFetchingParticipants = (): boolean => {
        return this.fetchingParticipants;
    };

    @action
    public clearSearchQuery = (): void => {
        this.searchQuery = '';
        this.setActiveTab();
        this.fetchParticipants();
    };

    public trackSearchRegistrantsMixPanel = (): void => {
        this.trackingService.get().track(TRACKING_EVENTS.SEARCH_PARTICIPANTS, {
            'Event ID': this.eventId,
            'Event Name': this.globalStorage.getEvent()?.name.toString(),
            'Meeting ID': this.meetingId,
        });
    };

    @action
    public fetchParticipants = async (page: number = FIRST_PAGE_INDEX): Promise<void> => {
        runInAction(() => {
            this.fetchingParticipants = true;
        });

        try {
            const params: IControlMeetingRequestParams & IPaginationParams = {
                accountId: this.accountId,
                eventId: this.eventId,
                meetingId: this.meetingId,
                query: this.searchQuery?.trim() || '',
                size: this.pagingVM.getPageSize(),
                page: page,
                ignoreAttendees: !IGNORE_ATTENDEES,
            };

            const response = await this.controlDAO.getMeetingRegistrants(params);
            const mappedRegistrants = response.content.map(mapRegistrantDtoToRegistrant);

            runInAction(() => {
                if (page === FIRST_PAGE_INDEX) {
                    this.items = mappedRegistrants;
                    this.loadedPagesCount = 1;
                    this.totalNumber = response.totalElements;
                    this.pagingVM.setTotalRecords(response.totalElements);
                } else {
                    this.items = [...this.items, ...mappedRegistrants];
                    this.loadedPagesCount = page + 1;
                }
            });
        } catch (e) {
            handleApiException(e);
        } finally {
            runInAction(() => {
                this.fetchingParticipants = false;
            });
        }
    };

    public loadMoreParticipants = async (): Promise<void> => {
        if (!this.hasMoreParticipants || this.fetchingParticipants) {
            return;
        }
        await this.fetchParticipants(this.loadedPagesCount);
    };
}

export { ParticipantsLiveFormRegistrantsTabViewModel };